import { ACTION_ICONS } from "../../config/siteConfig";
import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import { Modal } from "react-bootstrap";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import ContentLoader from "../loader/ContentLoader";
import DetailView from "../location/DetailView";
import DContext from "../../context/DContext";
import { bulkInventoryUploadService } from "../../services/bulk-inventory-upload.service";
import { suppliersService } from "../../services/suppliers.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";

const toArray = (resData) =>
  Array.isArray(resData) ? resData : resData?.data || [];

const PROCESS_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "processing", label: "Processing" },
  { value: "parsed", label: "Parsed" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
];

const PROCESS_STATUS_COLORS = {
  pending: { bg: "#fff4e6", color: "#e8590c" },
  processing: { bg: "#e7f5ff", color: "#1971c2" },
  parsed: { bg: "#f3e8ff", color: "#7048e8" },
  completed: { bg: "#d4edda", color: "#155724" },
  failed: { bg: "#f8d7da", color: "#721c24" },
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const formatDateTime = (d) =>
  d ? String(d).replace("T", " ").slice(0, 19) : "-";

// error_summary is a list of { error } objects
const errorSummaryText = (errors) =>
  Array.isArray(errors) && errors.length > 0
    ? errors
        .map((e) => e?.error)
        .filter(Boolean)
        .join("; ")
    : "";

const statusLabel = (status) =>
  PROCESS_STATUS_OPTIONS.find((o) => o.value === status)?.label ||
  status ||
  "-";

const BulkInventoryUploadList = () => {
  const { start, complete } = useLoadingBar();
  const { authState } = useContext(DContext);
  const isSupplierUser = String(authState?.user_role || "")
    .toLowerCase()
    .includes("supplier");

  const [loading, setLoading] = useState(false);
  const [uploadRes, setUploadRes] = useState({
    pagination: {},
    files: [],
  });

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  const initialFilters = {
    supplier_id: "",
    process_status: "",
  };
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const [show, setShow] = useState(false);
  const [viewFile, setViewFile] = useState(null);

  const [supplierOptions, setSupplierOptions] = useState([]);

  useEffect(() => {
    loadUploads();
  }, [pagingLimit, pageValue, appliedFilters]);

  useEffect(() => {
    if (!isSupplierUser) loadSupplierOptions();
  }, [isSupplierUser]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadSupplierOptions = async () => {
    try {
      const res = await suppliersService.list({
        page: 1,
        status: "active",
      });
      setSupplierOptions(
        toArray(res?.resData).map((s) => ({
          value: String(s.id),
          label: `${s.agency_name} (${s.supplier_code})`,
        })),
      );
    } catch (err) {
      setSupplierOptions([]);
    }
  };

  const loadUploads = async () => {
    try {
      setLoading(true);
      start();
      const res = await bulkInventoryUploadService.listUploadHistory({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setUploadRes({
          pagination: res.resData?.pagination || {},
          files: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const handleViewFile = (file) => {
    setViewFile(file);
    setShow(true);
  };

  const handleApply = (e) => {
    e?.preventDefault();
    setPageValue(1);
    setAppliedFilters({ ...filters });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setPageValue(1);
    setAppliedFilters(initialFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const handlePagination = (pageValueInput) => {
    setPageValue(pageValueInput);
  };

  const files = uploadRes?.files || [];
  const totalRecords = uploadRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const completedCount = files.filter(
    (v) => v?.process_status === "completed",
  ).length;
  const failedCount = files.filter(
    (v) => v?.process_status === "failed",
  ).length;
  const fromRecord =
    totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5)
      return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [
      ...new Set([
        1,
        2,
        totalPage - 1,
        totalPage,
        pageValue - 1,
        pageValue,
        pageValue + 1,
      ]),
    ]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "File Name", key: "file_name" },
    { label: "Supplier Code", key: "supplier_code" },
    { label: "File Type", key: "file_type" },
    { label: "Total Rows", key: "total_rows" },
    { label: "Success Rows", key: "success_rows" },
    { label: "Failed Rows", key: "failed_rows" },
    { label: "Skipped Rows", key: "skipped_rows" },
    { label: "Status", key: "process_status" },
    { label: "Error Summary", key: "error_summary" },
    { label: "Uploaded At", key: "created_at" },
    { label: "Processed At", key: "processed_at" },
  ];

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const actionBtn = (bg, color) => ({
    width: 32,
    height: 32,
    borderRadius: 8,
    background: bg,
    color,
    border: "none",
    padding: 0,
  });

  const statusBadge = (status) => {
    const c = PROCESS_STATUS_COLORS[status] || {
      bg: "#eef1f6",
      color: "#6b7a90",
    };
    return (
      <span
        style={{
          display: "inline-block",
          padding: "4px 10px",
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 600,
          background: c.bg,
          color: c.color,
        }}
      >
        {statusLabel(status)}
      </span>
    );
  };

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4
              className="m-0 fw-bold"
              style={{ color: "var(--page-heading)", fontSize: 20 }}
            >
              Bulk Inventory Uploads List
            </h4>
            <nav
              className="d-flex align-items-center gap-1 mt-1"
              style={{ fontSize: 12, color: "#475569" }}
            >
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Inventory</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Bulk Upload List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link
              to="/inventory/bulk-inventory-upload"
              className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
              style={{
                background: "var(--btn-bg)",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              <i className="bi bi-cloud-arrow-up"></i> Bulk Upload
            </Link>
            <CSVLink
              data={files.map((f) => ({
                ...f,
                error_summary: errorSummaryText(f.error_summary),
              }))}
              headers={csvHeaders}
              filename="bulk-inventory-uploads.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{
                background: "#fff",
                border: "1px solid #e8edf5",
                borderRadius: 8,
                height: 34,
                fontSize: 13,
                color: "#444",
              }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form
          onSubmit={handleApply}
          className="card border-0 shadow-sm mb-3"
          style={{ borderRadius: 14 }}
        >
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              {!isSupplierUser && (
                <div className="col-12 col-md">
                  <Select
                    options={supplierOptions}
                    value={
                      supplierOptions.find(
                        (o) => o.value === filters.supplier_id,
                      ) || null
                    }
                    onChange={(o) =>
                      setFilters({ ...filters, supplier_id: o ? o.value : "" })
                    }
                    placeholder="Select Supplier"
                    isClearable
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                  />
                </div>
              )}
              <div className="col-12 col-md">
                <Select
                  options={PROCESS_STATUS_OPTIONS}
                  value={
                    PROCESS_STATUS_OPTIONS.find(
                      (o) => o.value === filters.process_status,
                    ) || null
                  }
                  onChange={(o) =>
                    setFilters({ ...filters, process_status: o ? o.value : "" })
                  }
                  placeholder="Select Status"
                  isClearable
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{
                    background: "var(--btn-bg)",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{
                    background: "#fff",
                    border: "1px solid #e8edf5",
                    borderRadius: 8,
                    height: 34,
                    fontSize: 13,
                    color: "#444",
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Uploads
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{completedCount}</b> Completed on this page
          </span>
          <span style={chip("#c92a2a", "#fff0f0", "#ffc9c9")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{failedCount}</b> Failed on this page
          </span>
        </div>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 14, overflow: "hidden" }}
        >
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", 
                      "File",
                      "Supplier",
                      "Rows",
                      "Status",
                      "Uploaded At",
                      "Processed At",
                      "Actions",
                    ].map((h, i) => (
                      <th
                        key={i}
                        style={{
                          fontSize: "11px",
                          fontWeight: "700",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.6px",
                          borderBottom: "2px solid #e8edf5",
                          padding: "10px 12px",
                          whiteSpace: "nowrap",
                          background: "#f8f9fe",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {files.map((file, sno_i) => (
                    <tr
                      key={file?.id}
                      style={{ borderBottom: "1px solid #f0f2f5" }}
                    >
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td className="ps-3">
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 36,
                              height: 36,
                              background: "#e8f0fe",
                              flexShrink: 0,
                            }}
                          >
                            <i
                              className="bi bi-file-earmark-spreadsheet text-primary"
                              style={{ fontSize: 14 }}
                            ></i>
                          </div>
                          <div>
                            <div
                              className="fw-semibold text-dark"
                              style={{ fontSize: 13 }}
                            >
                              {file?.file_name}
                            </div>
                            <div
                              className="text-muted"
                              style={{ fontSize: 11 }}
                            >
                              {file?.file_type}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="fw-medium" style={{ fontSize: 13 }}>
                        {file?.supplier_code}
                      </td>
                      <td>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {file?.total_rows ?? 0} total
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          <span style={{ color: "#0f8a5f" }}>
                            {file?.success_rows ?? 0} ok
                          </span>
                          {" · "}
                          <span style={{ color: "#d93025" }}>
                            {file?.failed_rows ?? 0} failed
                          </span>
                          {" · "}
                          {file?.skipped_rows ?? 0} skipped
                        </div>
                      </td>
                      <td>{statusBadge(file?.process_status)}</td>
                      <td style={{ fontSize: 13 }}>
                        {formatDateTime(file?.created_at)}
                      </td>
                      <td style={{ fontSize: 13 }}>
                        {formatDateTime(file?.processed_at)}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewFile(file)}
                            title="View"
                            style={actionBtn("#e6f7f1", "#0f8a5f")}
                          >
                            <i
                              className={`bi ${ACTION_ICONS.view}`}
                              data-action="view"
                              style={{ fontSize: 13 }}
                            ></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && files.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        className="text-center text-muted py-4"
                        style={{ fontSize: 13 }}
                      >
                        No uploads found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{
              borderTop: "1px solid #eef1f6",
              fontSize: 13,
              color: "#6b7a90",
            }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> uploads
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button
                type="button"
                style={pageBtn(false, false)}
                onClick={handleGoPage}
              >
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => handlePagination(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button
                      type="button"
                      key={n}
                      style={pageBtn(n === pageValue, false)}
                      onClick={() => handlePagination(n)}
                    >
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => handlePagination(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <Modal size="lg" centered show={show} onHide={() => setShow(false)}>
          <Modal.Header
            closeButton
            closeVariant="white"
            style={{
              background:
                "linear-gradient(135deg, var(--btn-bg) 0%, var(--btn-hover-bg) 100%)",
              borderBottom: "none",
            }}
          >
            <Modal.Title
              className="d-flex align-items-center gap-2"
              style={{ color: "#fff", fontSize: 18, fontWeight: 600 }}
            >
              <i className={`bi ${ACTION_ICONS.view}`} data-action="view"></i>{" "}
              Upload Details
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: 24, background: "#f8f9fe" }}>
            {!viewFile ? (
              <ContentLoader loaderType="circleSpin" />
            ) : (
              <DetailView
                fields={[
                  { label: "File Name", value: viewFile.file_name },
                  { label: "File Type", value: viewFile.file_type },
                  { label: "Supplier Code", value: viewFile.supplier_code },
                  { label: "Total Rows", value: viewFile.total_rows },
                  { label: "Success Rows", value: viewFile.success_rows },
                  { label: "Failed Rows", value: viewFile.failed_rows },
                  { label: "Skipped Rows", value: viewFile.skipped_rows },
                  {
                    label: "Error Summary",
                    full: true,
                    value: viewFile.error_summary?.length ? (
                      <ul className="mb-0 ps-3" style={{ fontWeight: 500 }}>
                        {viewFile.error_summary.map((e, i) => (
                          <li key={i}>{e?.error}</li>
                        ))}
                      </ul>
                    ) : null,
                  },
                  {
                    label: "Uploaded At",
                    value: formatDateTime(viewFile.created_at),
                  },
                  {
                    label: "Parsed At",
                    value: formatDateTime(viewFile.parsed_at),
                  },
                  {
                    label: "Processed At",
                    value: formatDateTime(viewFile.processed_at),
                  },
                  {
                    label: "Status",
                    value: statusLabel(viewFile.process_status),
                    badge: true,
                  },
                ]}
              />
            )}
          </Modal.Body>
        </Modal>
      </section>
    </>
  );
};

export default BulkInventoryUploadList;
